import { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Zap, Store, MessageSquare, User, Wallet, LogOut, LayoutDashboard, Menu, X, Mail, Bell, BellRing, Smartphone, Ticket } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useLiveSocket } from "@/lib/useLiveSocket";
import { pushSupported, getPushState, subscribePush, unsubscribePush } from "@/lib/push";
import { Logo } from "@/components/common";
import InstallPrompt from "@/components/InstallPrompt";

export default function Layout({ children }) {
  const { user, logout, refresh } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [menu, setMenu] = useState(false);
  const [unread, setUnread] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [pushState, setPushState] = useState("default");
  const [pushBusy, setPushBusy] = useState(false);
  const isAdmin = user && user.role === "admin";

  useEffect(() => {
    if (user && pushSupported()) getPushState().then(setPushState);
  }, [user]);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushState === "subscribed") {
        const s = await unsubscribePush();
        setPushState(s);
        toast.success("Phone alerts turned off");
      } else {
        const s = await subscribePush();
        setPushState(s);
        toast.success("Phone alerts on — you'll get pop-ups even when the app is closed");
      }
    } catch (e) {
      toast.error(e.message || "Couldn't update phone alerts");
      setPushState(await getPushState());
    } finally { setPushBusy(false); }
  };

  useEffect(() => {
    if (!user) return;
    const load = () => {
      api.get("/dm-unread-count").then(({ data }) => setUnread(data.count)).catch(() => {});
      if (loc.pathname === "/chat") {
        api.post("/chat/read").then(() => setChatUnread(0)).catch(() => {});
      } else {
        api.get("/chat/unread-count").then(({ data }) => setChatUnread(data.count)).catch(() => {});
      }
    };
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [user, loc.pathname]);

  useLiveSocket(useCallback((evt) => {
    if (evt.type === "dm") {
      api.get("/dm-unread-count").then(({ data }) => setUnread(data.count)).catch(() => {});
      const m = evt.message;
      if (evt.from_role === "admin" && m && user && m.to_id === user.id && loc.pathname !== `/messages/${m.from_id}`) {
        toast(`Message from ${evt.from_name || "the host"}`, {
          description: m.text.length > 140 ? `${m.text.slice(0, 137)}…` : m.text,
          duration: 10000,
          action: { label: "Open", onClick: () => nav(`/messages/${m.from_id}`) },
        });
      }
    } else if (evt.type === "chat") {
      const senderId = evt.message?.user_id;
      if (loc.pathname === "/chat") { api.post("/chat/read").catch(() => {}); return; }
      if (user && senderId && senderId === user.id) return;
      setChatUnread((c) => c + 1);
    } else if (evt.type === "credit") {
      refresh();
      toast.success("Store credit updated!");
    }
  }, [refresh, loc.pathname, user, nav]));

  const openNotifs = () => setNotifOpen((o) => !o);

  const active = (p) => loc.pathname === p || (p !== "/" && loc.pathname.startsWith(p));

  const navLinks = [
    { to: "/drops", label: "Drops", icon: Zap },
    { to: "/shop", label: "Shop", icon: Store },
    { to: "/chat", label: "Chat", icon: MessageSquare },
  ];

  const doLogout = () => { logout(); nav("/"); };

  return (
    <div className="App min-h-screen bg-obsidian noise-overlay">
      {/* Top header */}
      <header className="sticky top-0 z-50 glass border-b border-gold/15 safe-top safe-x" data-testid="top-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
                className={`relative px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${active(l.to) ? "text-gold bg-gold/10" : "text-slate-300 hover:text-white"}`}>
                {l.label}
                {l.to === "/chat" && chatUnread > 0 && <span className="absolute top-0.5 right-1 bg-gold text-obsidian text-[9px] font-bold min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center" data-testid="nav-chat-badge">{chatUnread > 9 ? "9+" : chatUnread}</span>}
              </Link>
            ))}
            {isAdmin && (
              <Link to="/host" data-testid="nav-host" className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${active("/host") ? "text-gold bg-gold/10" : "text-slate-300 hover:text-white"}`}>
                Host
              </Link>
            )}
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <div className="relative">
                  <button onClick={openNotifs} className="relative p-2 text-slate-300 hover:text-gold" data-testid="nav-notifications">
                    <Bell className="w-5 h-5" />
                  </button>
                  {notifOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                      <div className="fixed left-3 right-3 top-[calc(4rem+env(safe-area-inset-top))] w-auto sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 max-h-[70vh] sm:max-h-96 overflow-y-auto custom-scrollbar glass border border-gold/20 rounded-2xl z-50 p-2" data-testid="notifications-panel">
                        <p className="text-xs font-mono text-gold/70 px-3 py-2 tracking-widest">PHONE ALERTS</p>
                        {pushSupported() && pushState !== "unsupported" && (
                          <button onClick={togglePush} disabled={pushBusy || pushState === "denied"} className="w-full flex items-center gap-3 px-3 py-2.5 mb-1 rounded-xl border border-gold/20 bg-gold/5 hover:bg-gold/10 disabled:opacity-60" data-testid="push-toggle">
                            <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${pushState === "subscribed" ? "gold-gradient text-obsidian" : "bg-vault text-gold"}`}>
                              {pushState === "subscribed" ? <BellRing className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
                            </span>
                            <span className="text-left flex-1">
                              <span className="block text-sm font-semibold text-white">
                                {pushState === "denied" ? "Phone alerts blocked" : pushState === "subscribed" ? "Phone alerts on" : "Get alerts on your phone"}
                              </span>
                              <span className="block text-[11px] text-slate-400">
                                {pushState === "denied" ? "Enable in browser settings" : pushState === "subscribed" ? "Tap to turn off pop-ups" : "Pop-ups even when app is closed"}
                              </span>
                            </span>
                          </button>
                        )}
                        {pushState === "subscribed" && (
                          <button onClick={async () => { try { await api.post("/push/test"); toast.success("Test alert sent — check your device"); } catch { toast.error("Couldn't send test alert"); } }} className="w-full flex items-center justify-center gap-2 px-3 py-2 mb-1 rounded-xl text-xs font-semibold text-gold hover:bg-gold/10 border border-gold/15" data-testid="push-test">
                            <BellRing className="w-3.5 h-3.5" /> Send me a test alert
                          </button>
                        )}
                        <p className="text-slate-500 text-[11px] text-center px-3 py-3 leading-relaxed">Alerts pop up on your phone — new drops, when a drop you're in fills up, and when the wheel's about to spin.</p>
                      </div>
                    </>
                  )}
                </div>
                <Link to="/messages" className="relative p-2 text-slate-300 hover:text-gold" data-testid="nav-messages">
                  <Mail className="w-5 h-5" />
                  {unread > 0 && <span className="absolute -top-0.5 -right-0.5 bg-gold text-obsidian text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{unread}</span>}
                </Link>
                <Link to="/wallet" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg card-surface text-sm font-mono text-gold" data-testid="nav-wallet">
                  <Wallet className="w-4 h-4" /> ${user.store_credit?.toFixed(2)}
                </Link>
                <button onClick={() => setMenu(!menu)} className="md:hidden p-2 text-white" data-testid="mobile-menu-toggle">
                  {menu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
                <Link to="/account" className="hidden md:flex w-9 h-9 rounded-full gold-gradient items-center justify-center text-obsidian font-bold" data-testid="nav-account">
                  {user.name?.[0]?.toUpperCase() || "U"}
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="px-4 py-2 text-sm font-semibold text-slate-200 hover:text-white" data-testid="nav-login">Log in</Link>
                <Link to="/signup" className="gold-gradient text-obsidian text-sm font-bold px-4 py-2 rounded-lg" data-testid="nav-signup">Sign up</Link>
              </>
            )}
          </div>
        </div>
        {menu && user && (
          <div className="md:hidden glass border-t border-gold/15 px-4 py-3 space-y-1" data-testid="mobile-menu">
            {[...navLinks, ...(isAdmin ? [{ to: "/host", label: "Host", icon: LayoutDashboard }] : [])].map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMenu(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-200 hover:bg-gold/10">
                <l.icon className="w-5 h-5 text-gold" /> {l.label}
              </Link>
            ))}
            <Link to="/wallet" onClick={() => setMenu(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-200"><Wallet className="w-5 h-5 text-gold" /> Wallet · ${user.store_credit?.toFixed(2)}</Link>
            <Link to="/orders" onClick={() => setMenu(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-200"><User className="w-5 h-5 text-gold" /> My Orders</Link>
            <Link to="/account" onClick={() => setMenu(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-200"><User className="w-5 h-5 text-gold" /> Account</Link>
            <button onClick={doLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-400 w-full"><LogOut className="w-5 h-5" /> Log out</button>
          </div>
        )}
      </header>

      <main className="pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-8 safe-x">{children}</main>

      {/* Mobile bottom nav */}
      {user ? (
        <nav className="fixed bottom-0 left-0 right-0 z-50 glass border-t border-gold/20 md:hidden safe-bottom safe-x" data-testid="bottom-nav">
          <div className="h-16 flex items-center justify-around">
          {[
            { to: "/drops", label: "Drops", icon: Zap },
            isAdmin ? { to: "/host", label: "Host", icon: LayoutDashboard } : { to: "/tickets", label: "Tickets", icon: Ticket },
            { to: "/chat", label: "Chat", icon: MessageSquare, badge: chatUnread },
            { to: "/account", label: "You", icon: User },
          ].map((l) => (
            <Link key={l.to} to={l.to} data-testid={`bottomnav-${l.label.toLowerCase()}`}
              className={`flex flex-col items-center gap-0.5 text-[10px] font-semibold ${active(l.to) && l.to !== "/" || loc.pathname === l.to ? "text-gold" : "text-slate-400"}`}>
              <span className="relative">
                <l.icon className="w-5 h-5" />
                {l.badge > 0 && <span className="absolute -top-1.5 -right-2.5 bg-gold text-obsidian text-[9px] font-bold min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center shadow-lg" data-testid="bottomnav-chat-badge">{l.badge > 9 ? "9+" : l.badge}</span>}
              </span>
              {l.label}
            </Link>
          ))}
          </div>
        </nav>
      ) : (
        <nav className="fixed bottom-0 left-0 right-0 z-50 glass border-t border-gold/20 md:hidden safe-bottom safe-x" data-testid="bottom-nav-guest">
          <div className="h-16 flex items-center justify-around">
          {[
            { to: "/", label: "Home", icon: Home },
            { to: "/shop", label: "Shop", icon: Store },
            { to: "/login", label: "Log in", icon: User },
          ].map((l) => (
            <Link key={l.to} to={l.to} data-testid={`guestnav-${l.label.toLowerCase().replace(/\s/g, "")}`}
              className={`flex flex-col items-center gap-0.5 text-[10px] font-semibold ${loc.pathname === l.to ? "text-gold" : "text-slate-400"}`}>
              <l.icon className="w-5 h-5" /> {l.label}
            </Link>
          ))}
          </div>
        </nav>
      )}

      <InstallPrompt />
    </div>
  );
}
